// src/utils/exportKiemSoatToWord.js
// ====================================================================
// PHIẾU KIỂM SOÁT (SO-style) – merge với public/phieuKiemsoat.docx
// - Có recalc totals theo danh sách sản phẩm đã chọn
// - Thêm company_businessCode, company_representativePersonalId, placeIssue
// - Items hỗ trợ thông tin hoá chất nếu có (chemicalName, chemicalFormula, casNumber)
// ====================================================================

import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';

// Firebase v9 modular
import { getApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, collection, query, where, limit, getDocs,
} from 'firebase/firestore';

/* -------------------- Helpers -------------------- */
const toDate = (v) => {
  if (!v) return null;
  try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return null; }
};
const pad2 = (n) => String(n).padStart(2, '0');
const num  = (v) => Number(v ?? 0);
const VND  = (n) => new Intl.NumberFormat('vi-VN', { style:'currency', currency:'VND' }).format(num(n));
const UPPER = (s) => String(s ?? '').toLocaleUpperCase('vi-VN');
const pick = (obj, paths, fallback = '') => {
  for (const p of paths) {
    const val = p.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
    if (val !== undefined && val !== null && val !== '') return val;
  }
  return fallback;
};
const publicURL = (name = 'phieuKiemsoat.docx') => `${window.location.origin}/${name}`;

/* ---- Number → chữ Việt ---- */
const VN_DIGITS = ['không','một','hai','ba','bốn','năm','sáu','bảy','tám','chín'];
const VN_UNITS  = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ', 'tỷ tỷ'];

function readTriplet(n, { showZeroHundred = false } = {}) {
  const h = Math.floor(n / 100), t = Math.floor((n % 100) / 10), u = n % 10;
  const parts = [];
  if (h > 0) parts.push(VN_DIGITS[h], 'trăm');
  else if (showZeroHundred) parts.push('không', 'trăm');

  if (t > 1) {
    parts.push(VN_DIGITS[t], 'mươi');
    if (u === 1) parts.push('mốt');
    else if (u === 4) parts.push('tư');
    else if (u === 5) parts.push('lăm');
    else if (u > 0) parts.push(VN_DIGITS[u]);
  } else if (t === 1) {
    parts.push('mười');
    if (u === 5) parts.push('lăm');
    else if (u > 0) parts.push(VN_DIGITS[u]);
  } else if (u > 0) {
    if (h > 0 || showZeroHundred) parts.push('lẻ');
    parts.push(VN_DIGITS[u]);
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}
function numberToWordsVI(n) {
  let x = Math.round(Number(n || 0));
  if (x === 0) return 'không';
  let sign = '';
  if (x < 0) { sign = 'âm '; x = Math.abs(x); }
  const groups = [];
  while (x > 0) { groups.push(x % 1000); x = Math.floor(x / 1000); }
  const top = groups.length - 1;
  const out = [];
  for (let i = top; i >= 0; i--) {
    const val = groups[i];
    if (val === 0) continue;
    const showZeroHundred = (i < top) && (val < 100) && (val % 100 > 0);
    const chunk = readTriplet(val, { showZeroHundred });
    if (chunk) {
      out.push(chunk);
      if (VN_UNITS[i]) out.push(VN_UNITS[i]);
    }
  }
  return out.join(' ').replace(/\s+/g, ' ').trim();
}
function moneyToWordsVI(n) {
  const w = numberToWordsVI(n);
  if (!w) return '';
  return w.charAt(0).toUpperCase() + w.slice(1) + ` đồng${Number(n) % 1 === 0 ? ' chẵn' : ''}`;
}

/* ---- Lỗi Docxtemplater chi tiết ---- */
function formatDocxError(err) {
  const props = err?.properties;
  if (Array.isArray(props?.errors) && props.errors.length) {
    return props.errors.map((e, i) => {
      const tag =
        e.properties?.name ||
        e.properties?.id ||
        e.properties?.xmlTag ||
        (e.properties?.explanation || '').match(/\{[^}]+\}/)?.[0] ||
        'unknown';
      const where = [
        e.properties?.file,
        e.properties?.position?.start != null ? `offset ${e.properties.position.start}` : '',
      ].filter(Boolean).join(' ');
      const why = e.properties?.explanation || e.message || 'Unknown';
      return `#${i + 1} ${tag}: ${why}${where ? ` (${where})` : ''}`;
    }).join('\n');
  }
  return err?.message || String(err);
}

/* -------------------- Firestore: customers & products -------------------- */
async function fetchOneById(db, col, id) {
  const snap = await getDoc(doc(db, col, String(id)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
async function fetchOneByField(db, col, field, value) {
  const qs = await getDocs(query(collection(db, col), where(field, '==', value), limit(1)));
  if (!qs.empty) { const s = qs.docs[0]; return { id: s.id, ...s.data() }; }
  return null;
}

async function fetchCustomer(order) {
  try {
    const app = getApp(); const db = getFirestore(app);
    const byId = order?.customerId || (order?.customerRef && (order.customerRef.id || order.customerRef));
    if (byId) { const r = await fetchOneById(db, 'customers', byId); if (r) return r; }
    if (order?.customerCode) { const r = await fetchOneByField(db, 'customers', 'code', order.customerCode); if (r) return r; }
    if (order?.customerName) { const r = await fetchOneByField(db, 'customers', 'name', order.customerName); if (r) return r; }
  } catch (e) { console.warn('fetchCustomer error:', e); }
  return null;
}
async function attachCustomer(order) {
  if (!order) return order;
  if (order.customer && (order.customer.name || order.customer.address || order.customer.code)) return order;
  const c = await fetchCustomer(order);
  return c ? { ...order, customer: c } : order;
}

async function fetchProductByIdOrCode(db, it) {
  const id = it.productId || it.productRef?.id || it.productRef;
  if (id) {
    const r = await fetchOneById(db, 'products', id);
    if (r) return r;
  }
  const code = it.productCode || it.sku || it.code;
  if (code) {
    const r = await fetchOneByField(db, 'products', 'code', code);
    if (r) return r;
  }
  return null;
}
async function attachProducts(order) {
  try {
    const app = getApp(); const db = getFirestore(app);
    const items = Array.isArray(order?.items) ? [...order.items] : [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i] || {};
      if (it.chemicalName || it.chemicalFormula || it.casNumber) continue;
      const p = await fetchProductByIdOrCode(db, it);
      if (p) {
        items[i] = {
          ...it,
          chemicalName: p.chemicalName || p.name || it.chemicalName,
          chemicalFormula: p.chemicalFormula || it.chemicalFormula,
          casNumber: p.casNumber || p.CAS || it.casNumber,
        };
      }
    }
    return { ...order, items };
  } catch (e) {
    console.warn('attachProducts error:', e);
    return order;
  }
}

/* -------------------- Recalc totals from selected items -------------------- */
function recalcFinancials(order = {}) {
  const n = (v) => Number(v ?? 0);
  const items = Array.isArray(order.items) ? order.items : [];

  let subtotal = 0;
  const mapped = items.map((it) => {
    const q = n(it.quantity);
    const p = n(it.exportPrice ?? it.price ?? it.unitPrice ?? it.importPrice);
    const s = q * p;
    subtotal += s;
    return { ...it, __q: q, __p: p, __s: s };
  });

  const shippingFee     = n(order.shippingFee);
  const amountPaid      = n(order.amountPaid);
  const taxRate         = n(order.taxRate);
  let   discountRate    = n(order.discountRate);
  let   discountAmount  = n(order.discountAmount);

  if (discountRate > 0) {
    discountAmount = Math.round(subtotal * discountRate / 100);
  } else if (discountAmount > 0) {
    discountRate = subtotal ? (discountAmount / subtotal) * 100 : 0;
  }

  const taxableBase = Math.max(0, subtotal - discountAmount);
  const taxAmount   = Math.round(taxableBase * taxRate / 100);
  const grandTotal  = taxableBase + taxAmount + shippingFee;
  const remainingAmount = grandTotal - amountPaid;

  return {
    items: mapped,
    subtotal,
    discountRate,
    discountAmount,
    taxRate,
    taxAmount,
    shippingFee,
    grandTotal,
    amountPaid,
    remainingAmount,
  };
}

/* -------------------- Build data (SO-style) -------------------- */
function buildSOStyleData(order = {}, company = {}) {
  const soNumber = order.soNumber || order.poNumber || '';
  const soDate   = toDate(order.soDate || order.poDate || order.createdAt);

  // TÍNH LẠI theo items đã chọn
  const fin = recalcFinancials(order);

  const items = (fin.items || []).map((it, i) => {
    const chemName    = it.chemicalName || it.name || '';
    const chemFormula = it.chemicalFormula || '';
    const cas         = it.casNumber || it.CAS || '';
    return {
      idx: i + 1,
      productName: it.productName || it.name || chemName || '',
      quantity: it.__q,
      exportPrice: it.__p,
      exportPriceVND: VND(it.__p),
      lineTotal: it.__s,
      lineTotalVND: VND(it.__s),
      chemicalName: chemName,
      chemicalFormula: chemFormula,
      casNumber: cas,
      casNumbe: cas, // alias cho template gõ thiếu 'r'
    };
  });

  const rep  = pick(company, ['representative','legalRepresentative'], '');
  const bank = pick(company, ['bankAccount.number','bankAccount','accountNumber','bankAccountNo'], '');

  return {
    // so_*
    so_number: soNumber,
    so_day:    soDate ? pad2(soDate.getDate())       : '',
    so_month:  soDate ? pad2(soDate.getMonth() + 1)  : '',
    so_year:   soDate ? String(soDate.getFullYear()) : '',

    // company_*
    company_name: company.name || '',
    company_address: company.address || '',
    company_phone: company.phone || '',
    company_tax: company.taxCode || '',
    company_businessCode: company.businessCode || company.businessID || '',
    company_representative: rep,
    company_representative_upper: UPPER(rep),
    company_representativePersonalId:
      company.representativePersonalId || company.representativeId || '',
    company_placeIssue: company.placeIssue || '',
    company_bank_account: bank,
    company: {
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      taxCode: company.taxCode || '',
      businessCode: company.businessCode || company.businessID || '',
      representative: rep,
      representativeUpper: UPPER(rep),
      representativePersonalId: company.representativePersonalId || company.representativeId || '',
      placeIssue: company.placeIssue || '',
      bankAccount: bank,
    },

    // customer_*
    customer_name:          pick(order, ['customerName','customer.name'], ''),
    customer_address:       pick(order, ['customerAddress','customer.address','address'], ''),
    customer_code:          pick(order, ['customerCode','customer.code','code'], ''),
    customer_businessCode:  pick(order, ['customer.businessCode','businessCode'], ''),
    customer_representative:pick(order, ['customerRepresentative','customer.representative'], ''),
    customer_representativePersonalId:
                            pick(order, ['customer.representativePersonalId','representativePersonalId'], ''),
    customer_position:      pick(order, ['customerPosition','customer.position'], ''),
    customer: {
      name:          pick(order, ['customer.name','customerName'], ''),
      address:       pick(order, ['customer.address','customerAddress','address'], ''),
      code:          pick(order, ['customer.code','customerCode','code'], ''),
      businessCode:  pick(order, ['customer.businessCode','businessCode'], ''),
      representative:pick(order, ['customer.representative','customerRepresentative'], ''),
      representativePersonalId:
                      pick(order, ['customer.representativePersonalId','representativePersonalId'], ''),
      position:      pick(order, ['customer.position','customerPosition'], ''),
      phone:         pick(order, ['customer.phone'], ''),
      email:         pick(order, ['customer.email'], ''),
      note:          pick(order, ['customer.note'], ''),
    },

    // table + totals (ĐÃ TÍNH LẠI)
    items,
    subtotal: fin.subtotal,            subtotalVND: VND(fin.subtotal),
    discountRate: fin.discountRate,
    discountAmount: fin.discountAmount, discountAmountVND: VND(fin.discountAmount),
    taxRate: fin.taxRate,              taxAmount: fin.taxAmount,      taxAmountVND: VND(fin.taxAmount),
    shippingFee: fin.shippingFee,      shippingFeeVND: VND(fin.shippingFee),
    grandTotal: fin.grandTotal,        grandTotalVND: VND(fin.grandTotal),
    amountPaid: fin.amountPaid,        amountPaidVND: VND(fin.amountPaid),
    remainingAmount: fin.remainingAmount, remainingAmountVND: VND(fin.remainingAmount),

    // money in words
    grandTotalText:      moneyToWordsVI(fin.grandTotal),
    remainingAmountText: moneyToWordsVI(fin.remainingAmount),
  };
}

/* -------------------- Merge DOCX -------------------- */
async function mergeWithTemplate(data, templateName = 'phieuKiemsoat.docx') {
  const res = await fetch(publicURL(templateName));
  if (!res.ok) throw new Error(`Không tìm thấy /${templateName} trên Hosting`);
  const ab = await res.arrayBuffer();

  const zip = new PizZip(ab);
  const ok = Object.keys(zip.files || {}).some((k) => /^word\/document\.xml$/i.test(k));
  if (!ok) {
    const entries = Object.keys(zip.files || {});
    throw new Error(`Template không hợp lệ (thiếu word/document.xml). ZIP: ${entries.join(', ')}`);
  }

  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    nullGetter: () => '',
  });

  if (typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production') {
    console.log('[KIEMSOAT (SO-style)] keys:', Object.keys(data));
    console.log('[KIEMSOAT (SO-style)] first item:', (data.items || [])[0]);
  }

  doc.setData(data);

  try {
    doc.render();
  } catch (err) {
    console.error('KiemSoat docx render error:', err, err?.properties?.errors);
    throw new Error(`Placeholder sai/thiếu trong 'phieuKiemsoat.docx':\n${formatDocxError(err)}`);
  }

  return doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

/* ===================== Public API ===================== */
export async function exportKiemSoatDocx(order, company = {}) {
  try {
    const withCustomer = await attachCustomer(order);
    const withProducts = await attachProducts(withCustomer);
    const data = buildSOStyleData(withProducts, company);
    const blob = await mergeWithTemplate(data, 'phieuKiemsoat.docx');
    saveAs(blob, `KS_${data.so_number || 'phieu-kiem-soat'}.docx`);
  } catch (e) {
    console.error('Xuất Phiếu Kiểm soát DOCX lỗi:', e);
    alert(String(e?.message || e));
  }
}
